import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSellWizardStore } from '../store/sellWizardStore';
import { useCatalogStore } from '../store/catalogStore';
import { createAccount, updateAccount, uploadScreenshots, verifyGameId, ApiError } from '../lib/api';
import { computeSkinTierCounts } from '../lib/points';
import SkinPicker from '../components/SkinPicker';
import CollabChip from '../components/CollabChip';
import AccountCard from '../components/AccountCard';
import MainButtonFallback from '../components/MainButtonFallback';
import { useDragScroll } from '../hooks/useDragScroll';
import type { Account } from '../types';
import { useToastStore } from '../store/toastStore';
import {
  hapticImpact,
  hapticNotification,
  hapticSelection,
  hideBackButton,
  hideMainButton,
  setBackButton,
  setMainButton,
} from '../lib/telegram';

const STEP_LABELS = ['Основное', 'Скины', 'Валюты', 'Скриншоты', 'Превью'];

export default function SellWizardPage() {
  const navigate = useNavigate();
  const { draft, files, editingId, setField, toggleSkin, addFiles, removeFile, goToStep, reset } =
    useSellWizardStore();
  const { skins, meta, load } = useCatalogStore();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [skinTab, setSkinTab] = useState<'legend' | 'grand_collab' | 'grand_series' | 'collector'>('legend');
  const [subFilter, setSubFilter] = useState<string | null>(null);
  const [verify, setVerify] = useState<{
    status: 'idle' | 'loading' | 'success' | 'error';
    name?: string;
    region?: string | null;
    message?: string;
  }>({ status: 'idle' });
  const showToast = useToastStore((s) => s.show);
  // Editing an existing (rejected) listing skips straight into the wizard — otherwise every visit
  // to /sell starts at an intro screen so an abandoned draft is never silently auto-resumed.
  const [phase, setPhase] = useState<'intro' | 'wizard'>(editingId ? 'wizard' : 'intro');

  useEffect(() => {
    load();
  }, [load]);

  const hasProgress =
    draft.step > 1 ||
    draft.price !== '' ||
    draft.gameId !== '' ||
    draft.serverId !== '' ||
    draft.server !== '' ||
    draft.rank !== '' ||
    draft.skinKeys.length > 0 ||
    draft.description !== '' ||
    files.length > 0;

  const step = Math.min(draft.step, STEP_LABELS.length);
  const selectedKeys = useMemo(() => new Set(draft.skinKeys), [draft.skinKeys]);

  // Screenshots aren't uploaded until final submit — the step-6 preview card needs a temporary
  // blob URL for the first picked file so it shows the real cover, not the placeholder icon.
  const previewCoverUrl = useMemo(() => (files[0] ? URL.createObjectURL(files[0]) : null), [files]);
  useEffect(() => {
    return () => {
      if (previewCoverUrl) URL.revokeObjectURL(previewCoverUrl);
    };
  }, [previewCoverUrl]);

  const totals = useMemo(
    () => computeSkinTierCounts(draft.skinKeys, skins.length ? new Map(skins.map((s) => [s.key, s])) : new Map()),
    [draft.skinKeys, skins]
  );
  const manualTotal = Number(draft.totalSkins) || 0;

  const tabSkins = useMemo(() => {
    let list = skins.filter((s) => s.tier === skinTab);
    if (subFilter) list = list.filter((s) => s.collab === subFilter || s.series === subFilter);
    return list;
  }, [skins, skinTab, subFilter]);

  const subGroups = useMemo(() => {
    if (skinTab === 'grand_collab') return meta?.collaborations.filter((c) => c.totalSkins > 0) ?? [];
    if (skinTab === 'grand_series') return meta?.series ?? [];
    return [];
  }, [meta, skinTab]);

  // Any edit to Game ID / Server ID invalidates a previous verification result.
  useEffect(() => {
    setVerify({ status: 'idle' });
  }, [draft.gameId, draft.serverId]);

  const canVerify = draft.gameId.trim() !== '' && draft.serverId.trim() !== '';

  const runVerify = async () => {
    if (!canVerify) return;
    hapticImpact('light');
    setVerify({ status: 'loading' });
    try {
      const res = await verifyGameId(draft.gameId.trim(), draft.serverId.trim());
      if (res.valid && res.name) {
        hapticNotification('success');
        setVerify({ status: 'success', name: res.name, region: res.region });
      } else {
        hapticNotification('error');
        setVerify({ status: 'error', message: 'Аккаунт не найден — проверь Game ID и Server ID' });
      }
    } catch {
      hapticNotification('error');
      setVerify({ status: 'error', message: 'Сервис проверки временно недоступен' });
    }
  };

  const canProceed = useMemo(() => {
    switch (step) {
      case 1:
        return Number(draft.price) > 0 && draft.gameId.trim() !== '' && draft.serverId.trim() !== '';
      case 2:
        return !!draft.collectorTier && (draft.collectorTier === 'world' || !!draft.collectorTierLevel);
      case 4:
        return files.length >= 3;
      case 5:
        return draft.confirmAccuracy;
      default:
        return true;
    }
  }, [step, draft, files]);

  // A useState-based guard isn't enough: React batches/delays the re-render, so two taps fired
  // before the first render flushes both see submitting===false and both go through. A ref
  // updates synchronously, so this is the only reliable single-flight lock.
  const submittingRef = useRef(false);
  const skinTabsDrag = useDragScroll<HTMLDivElement>();
  const subGroupsDrag = useDragScroll<HTMLDivElement>();

  const submit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const payload = {
        price: Number(draft.price),
        currency: draft.currency,
        gameId: draft.gameId.trim(),
        serverId: draft.serverId.trim(),
        server: draft.server,
        rank: draft.rank,
        heroesCount: draft.heroesCount === '' ? undefined : Number(draft.heroesCount),
        collectorTier: draft.collectorTier,
        collectorTierLevel: draft.collectorTierLevel === '' ? undefined : Number(draft.collectorTierLevel),
        totalSkins: manualTotal,
        description: draft.description || undefined,
        skinKeys: draft.skinKeys,
        recallStunning: draft.recallStunning,
        recallLuxurious: draft.recallLuxurious,
        towerSkins: draft.towerSkins,
        loadingStunning: draft.loadingStunning,
        loadingLuxurious: draft.loadingLuxurious,
        loadingStellar: draft.loadingStellar,
        diamonds: draft.diamonds,
        magicWheelProgress: draft.magicWheelProgress,
      };
      const result: Account = editingId ? await updateAccount(editingId, payload) : await createAccount(payload);
      if (files.length > 0) {
        await uploadScreenshots(result.id, files);
      }
      hapticNotification('success');
      setSubmitted(true);
      reset();
    } catch (e) {
      hapticNotification('error');
      showToast(e instanceof ApiError ? `Ошибка: ${e.message}` : 'Не удалось отправить объявление', 'error');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // MainButton is registered with Telegram's native bridge, not React — its onClick must always
  // see the latest `draft` (e.g. description typed after the button's disabled/text state last
  // changed), so the actual logic lives in a ref updated every render, not in the effect's closure.
  const onMainButtonClickRef = useRef<() => void>(() => {});
  onMainButtonClickRef.current = () => {
    if (submittingRef.current) return;
    if (!canProceed) {
      hapticNotification('error');
      return;
    }
    hapticImpact('light');
    if (step === 5) submit();
    else goToStep(step + 1);
  };

  useEffect(() => {
    if (submitted || phase !== 'wizard') {
      hideMainButton();
      return;
    }
    setMainButton({
      text: step === 5 ? 'Отправить на модерацию' : 'Далее',
      onClick: () => onMainButtonClickRef.current(),
      disabled: !canProceed,
      loading: submitting,
    });
  }, [step, canProceed, submitting, submitted, phase]);

  useEffect(() => {
    if (submitted) {
      hideBackButton();
      return;
    }
    setBackButton(() => {
      if (phase === 'intro') navigate('/');
      else if (step > 1) goToStep(step - 1);
      else setPhase('intro');
    });
    return () => hideBackButton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, submitted, phase]);

  if (submitted) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="text-5xl">🕓</div>
        <div className="text-lg font-semibold text-white">Объявление на модерации</div>
        <div className="text-sm text-white/60">Обычно проверка занимает до 12 часов.</div>
        <button
          onClick={() => navigate('/profile')}
          className="mt-4 rounded-full bg-mlbb-accent px-5 py-2.5 text-sm font-medium text-white"
        >
          Мои объявления
        </button>
      </div>
    );
  }

  if (phase === 'intro') {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="text-5xl">💼</div>
        {hasProgress ? (
          <>
            <div className="text-lg font-semibold text-white">Есть незавершённое объявление</div>
            <div className="text-sm text-white/60">
              Заполнено до шага {step}/{STEP_LABELS.length}. Можно продолжить с того же места или начать заново.
            </div>
            <div className="mt-2 flex w-full max-w-xs flex-col gap-3">
              <button
                onClick={() => {
                  hapticImpact('medium');
                  setPhase('wizard');
                }}
                className="rounded-full bg-mlbb-accent py-3 text-sm font-medium text-white"
              >
                ▶️ Продолжить заполнение
              </button>
              <button
                onClick={() => {
                  hapticImpact('light');
                  reset();
                  setPhase('wizard');
                }}
                className="rounded-full border border-mlbb-border py-3 text-sm text-white/70"
              >
                🔄 Начать заново
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-lg font-semibold text-white">Продать аккаунт</div>
            <div className="text-sm text-white/60">
              Заполни анкету за {STEP_LABELS.length} шагов: цена, скины, валюты, скриншоты. Мы промодерируем и
              опубликуем в каталоге.
            </div>
            <button
              onClick={() => {
                hapticImpact('medium');
                setPhase('wizard');
              }}
              className="mt-2 rounded-full bg-mlbb-accent px-6 py-3 text-sm font-medium text-white"
            >
              ➕ Создать объявление
            </button>
          </>
        )}
      </div>
    );
  }

  const previewAccount: Account = {
    id: 'preview',
    status: 'PENDING',
    price: Number(draft.price) || 0,
    currency: draft.currency,
    server: draft.server,
    rank: draft.rank,
    heroesCount: draft.heroesCount === '' ? null : Number(draft.heroesCount),
    collectorTier: draft.collectorTier || null,
    collectorTierLevel: draft.collectorTierLevel === '' ? null : Number(draft.collectorTierLevel),
    totalSkins: manualTotal,
    legendCount: totals.legendCount,
    grandCount: totals.grandCount,
    collectorCount: totals.collectorCount,
    recallStunning: draft.recallStunning,
    recallLuxurious: draft.recallLuxurious,
    towerSkins: draft.towerSkins,
    loadingStunning: draft.loadingStunning,
    loadingLuxurious: draft.loadingLuxurious,
    loadingStellar: draft.loadingStellar,
    diamonds: draft.diamonds,
    magicWheelProgress: draft.magicWheelProgress,
    description: draft.description || null,
    rejectReason: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    skins: draft.skinKeys
      .map((k) => skins.find((s) => s.key === k))
      .filter((s): s is NonNullable<typeof s> => !!s)
      .map((s) => ({ ...s, dbTier: s.tier })),
    screenshots: previewCoverUrl ? [{ id: 'preview', url: previewCoverUrl }] : [],
  };

  return (
    <div className="pb-28">
      <div className="sticky top-0 z-30 border-b border-mlbb-border bg-mlbb-bg/95 px-4 py-3 backdrop-blur-sm">
        <div className="mb-2 flex items-center justify-between text-xs">
          <button
            type="button"
            onClick={() => {
              hapticImpact('light');
              if (step > 1) goToStep(step - 1);
              else setPhase('intro');
            }}
            className="-ml-1 flex items-center gap-1 rounded-full px-1.5 py-0.5 text-white/70 active:bg-white/10"
          >
            ← Назад
          </button>
          <span className="text-white/50">
            Шаг {step}/{STEP_LABELS.length} — {STEP_LABELS[step - 1]}
          </span>
        </div>
        <div className="flex gap-1">
          {STEP_LABELS.map((label, i) => {
            const n = i + 1;
            const reachable = n <= step;
            return (
              <button
                key={n}
                type="button"
                disabled={!reachable}
                onClick={() => {
                  if (reachable && n !== step) {
                    hapticSelection();
                    goToStep(n);
                  }
                }}
                aria-label={`Шаг ${n}: ${label}`}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  reachable ? 'bg-mlbb-accent' : 'bg-mlbb-border'
                } ${reachable && n !== step ? 'cursor-pointer' : ''}`}
              />
            );
          })}
        </div>
      </div>

      <div className="space-y-4 p-4">
        {step === 1 && (
          <div className="space-y-3">
            <div className="rounded-xl border border-mlbb-border bg-mlbb-card p-3 text-xs text-white/50">
              🔒 Game ID и Server ID видны только вам и админам ARC TEAM — покупателям и в каталоге они не
              показываются.
            </div>
            <Field label="Game ID *">
              <input
                type="text"
                inputMode="numeric"
                value={draft.gameId}
                onChange={(e) => setField('gameId', e.target.value)}
                placeholder="123456789"
                className="input"
              />
            </Field>
            <Field label="Server ID *">
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={draft.serverId}
                  onChange={(e) => setField('serverId', e.target.value)}
                  placeholder="2222"
                  className="input flex-1"
                />
                <button
                  type="button"
                  onClick={runVerify}
                  disabled={!canVerify || verify.status === 'loading'}
                  className="shrink-0 rounded-lg border border-mlbb-border bg-mlbb-card px-3 text-sm text-white/80 disabled:opacity-40"
                >
                  {verify.status === 'loading' ? '...' : '🔍 Проверить'}
                </button>
              </div>
            </Field>
            {verify.status === 'success' && (
              <div className="rounded-xl border border-emerald-800 bg-emerald-950/40 p-3 text-sm text-emerald-200">
                ✅ Аккаунт найден: <span className="font-semibold">{verify.name}</span>
                {verify.region ? ` (${verify.region})` : ''} — проверь, что это твой аккаунт.
              </div>
            )}
            {verify.status === 'error' && (
              <div className="rounded-xl border border-red-800 bg-red-950/40 p-3 text-sm text-red-200">
                ❌ {verify.message ?? 'Не удалось подтвердить Game ID / Server ID'}
              </div>
            )}
            <Field label="Цена">
              <div className="flex gap-2">
                <input
                  type="number"
                  inputMode="numeric"
                  value={draft.price}
                  onChange={(e) => setField('price', e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="150"
                  className="input flex-1"
                />
                <select
                  value={draft.currency}
                  onChange={(e) => setField('currency', e.target.value as typeof draft.currency)}
                  className="input w-24"
                >
                  <option value="USD">USD</option>
                  <option value="RUB">RUB</option>
                  <option value="UZS">UZS</option>
                </select>
              </div>
            </Field>
            <Field label="Количество героев">
              <input
                type="number"
                value={draft.heroesCount}
                onChange={(e) => setField('heroesCount', e.target.value === '' ? '' : Number(e.target.value))}
                className="input"
              />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div>
              <div className="mb-2 text-sm text-white/70">Коллекция *</div>
              <div className="grid grid-cols-4 gap-2">
                {(meta?.collectorTiers ?? []).map((tier) => (
                  <button
                    key={tier.id}
                    type="button"
                    onClick={() => {
                      hapticSelection();
                      const nextTier = draft.collectorTier === tier.id ? '' : tier.id;
                      setField('collectorTier', nextTier);
                      if (nextTier === 'world') setField('collectorTierLevel', '');
                    }}
                    className={`flex flex-col items-center gap-1 rounded-xl border p-2 ${
                      draft.collectorTier === tier.id
                        ? 'border-mlbb-accent bg-mlbb-accent/10'
                        : 'border-mlbb-border bg-mlbb-card'
                    }`}
                  >
                    <img src={tier.icon} alt="" className="h-12 w-12 rounded-full object-cover" />
                    <span className="text-center text-[10px] leading-tight text-white/70">{tier.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {draft.collectorTier && draft.collectorTier !== 'world' && (
              <div>
                <div className="mb-2 text-sm text-white/70">Уровень коллекции *</div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((lvl) => (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => {
                        hapticSelection();
                        setField('collectorTierLevel', draft.collectorTierLevel === lvl ? '' : lvl);
                      }}
                      className={`flex-1 rounded-xl border py-2.5 text-sm font-medium ${
                        draft.collectorTierLevel === lvl
                          ? 'border-mlbb-accent bg-mlbb-accent/10 text-white'
                          : 'border-mlbb-border bg-mlbb-card text-white/60'
                      }`}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div
              ref={skinTabsDrag.ref}
              onMouseDown={skinTabsDrag.onMouseDown}
              onMouseMove={skinTabsDrag.onMouseMove}
              onMouseUp={skinTabsDrag.onMouseUp}
              onMouseLeave={skinTabsDrag.onMouseLeave}
              onClickCapture={skinTabsDrag.onClickCapture}
              className={`flex gap-2 overflow-x-auto pb-1 ${skinTabsDrag.className}`}
              style={{ scrollbarWidth: 'none' }}
            >
              {(
                [
                  ['legend', '👑 Legend'],
                  ['grand_collab', '⭐ Коллаборации'],
                  ['grand_series', '⭐ Серии'],
                  ['collector', '💠 Collector'],
                ] as const
              ).map(([tier, label]) => (
                <button
                  key={tier}
                  onClick={() => {
                    setSkinTab(tier);
                    setSubFilter(null);
                  }}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${
                    skinTab === tier ? 'bg-mlbb-accent text-white' : 'bg-mlbb-card text-white/60'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {subGroups.length > 0 && (
              <div
                ref={subGroupsDrag.ref}
                onMouseDown={subGroupsDrag.onMouseDown}
                onMouseMove={subGroupsDrag.onMouseMove}
                onMouseUp={subGroupsDrag.onMouseUp}
                onMouseLeave={subGroupsDrag.onMouseLeave}
                onClickCapture={subGroupsDrag.onClickCapture}
                className={`flex gap-2 overflow-x-auto pb-1 ${subGroupsDrag.className}`}
                style={{ scrollbarWidth: 'none' }}
              >
                <CollabChip label="Все" selected={!subFilter} onClick={() => setSubFilter(null)} />
                {subGroups.map((g) => (
                  <CollabChip
                    key={g.id}
                    label={g.name}
                    icon={g.logo}
                    selected={subFilter === g.id}
                    onClick={() => setSubFilter(g.id)}
                  />
                ))}
              </div>
            )}

            <SkinPicker skins={tabSkins} selectedKeys={selectedKeys} onToggle={toggleSkin} />

            <Field label="Всего скинов на аккаунте (включая мелкие)">
              <input
                type="number"
                value={draft.totalSkins}
                onChange={(e) => setField('totalSkins', e.target.value === '' ? '' : Number(e.target.value))}
                className="input"
              />
            </Field>

            <div className="sticky bottom-0 rounded-xl border border-mlbb-border bg-mlbb-card p-3 text-sm text-white">
              Выбрано: {draft.skinKeys.length} скинов
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <Field label="💎 Алмазы">
              <input
                type="number"
                value={draft.diamonds}
                onChange={(e) => setField('diamonds', Number(e.target.value) || 0)}
                className="input"
              />
            </Field>
            <Field label={`🎡 Magic Wheel: ${draft.magicWheelProgress}/200`}>
              <input
                type="range"
                min={0}
                max={200}
                value={draft.magicWheelProgress}
                onChange={(e) => setField('magicWheelProgress', Number(e.target.value))}
                className="w-full"
              />
            </Field>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            <div className="rounded-xl border border-mlbb-border bg-mlbb-card p-3 text-sm text-white/60">
              Нужно минимум 3 скриншота: профиль, коллекция скинов, инвентарь скинов. Можно добавить до 8.
            </div>
            <label className="flex cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-mlbb-border py-8 text-sm text-white/50">
              📷 Добавить скриншоты
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) addFiles(Array.from(e.target.files));
                  e.target.value = '';
                }}
              />
            </label>
            {files.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {files.map((f, i) => (
                  <div key={i} className="relative aspect-square overflow-hidden rounded-lg">
                    <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                    <button
                      onClick={() => removeFile(i)}
                      className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-xs text-white"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-xs text-white/40">{files.length}/8 загружено</div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <AccountCard account={previewAccount} />

            {manualTotal !== draft.skinKeys.length && (
              <div className="rounded-xl border border-mlbb-border bg-mlbb-card p-3 text-xs text-white/50">
                Всего скинов указано вручную: {manualTotal} (выбрано из справочника: {draft.skinKeys.length})
              </div>
            )}

            <Field label="Описание">
              <textarea
                value={draft.description}
                onChange={(e) => setField('description', e.target.value)}
                rows={4}
                placeholder="Расскажи об аккаунте подробнее..."
                className="input resize-none"
              />
            </Field>

            <label className="flex items-start gap-2 text-sm text-white/70">
              <input
                type="checkbox"
                checked={draft.confirmAccuracy}
                onChange={(e) => setField('confirmAccuracy', e.target.checked)}
                className="mt-0.5"
              />
              Подтверждаю достоверность данных
            </label>
          </div>
        )}
      </div>

      <MainButtonFallback
        text={step === 5 ? 'Отправить на модерацию' : 'Далее'}
        onClick={() => onMainButtonClickRef.current()}
        disabled={!canProceed}
        loading={submitting}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-sm text-white/70">{label}</div>
      {children}
    </div>
  );
}
